import React, { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useStateContext } from '../contexts/ContextProvider';

import operationsIcon from '../assets/images/operations_menu_icon.svg';
import bitdefenderLogo from '../assets/images/bitdefender_logo.svg';
import generalIcon from '../assets/images/general_menu_icon.svg';
import bitdefenderIcon from '../assets/images/normal_logo_bitdefender_b_u382.svg';
import { ApiEndpoints, LocationPaths } from '../utils/common';
import { getData } from '../utils/apiFunctions';

let submenuGeneralCollapsedItemExpanded = false;
let submenuOperationsCollapsedItemExpanded = false;

let currentActiveTargetName = null;

const Sidebar = () => {

  const { activeMenu,
    setActiveMenu,
    screenSize,
    user,
    isNewTenant,
    hasInventoryEvents,
    setHasInventoryEvents,
    unhandledInventoryEvents,
    setUnhandledInventoryEvents,
    hasAgentEvents,
    setHasAgentEvents,
    unhandledAgentEvents,
    setUnhandledAgentEvents } = useStateContext();

  const { data: inventoryEvents, isSuccess: isInventoryEventsCallSuccess } = useQuery({
    queryKey: ['fetchInventoryEvents'],
    queryFn: () => getData(`${ApiEndpoints.INVENTORY_EVENTS}`),
    enabled: user?.tenantInfo.integrationId !== null,
    initialData: []
  });
  const { data: agentEvents, isSuccess: isAgentEventsCallSuccess } = useQuery({
    queryKey: ['fetchAgentEvents'],
    queryFn: () => getData(`${ApiEndpoints.AGENT_EVENTS}`),
    enabled: user?.tenantInfo.integrationId !== null,
    initialData: []
  });

  const [submenuGeneralCollapsedActive, setSubmenuGeneralCollapsedActive] = useState(submenuGeneralCollapsedItemExpanded);
  const [submenuOperationsCollapsedActive, setSubmenuOperationsCollapsedActive] = useState(submenuOperationsCollapsedItemExpanded);
  const locationData = useLocation();

  let sidemenuGeneralRef = useRef(null);
  let sidemenuGeneralExpandedRef = useRef(null);

  let sidemenuGeneralCollapsedRef = useRef(null);
  let sidemenuGeneralCollapsedExpandedRef = useRef(null);
  let sidemenuOperationsCollapsedRef = useRef(null);
  let sidemenuOperationsCollapsedExpandedRef = useRef(null);

  useEffect(() => {
    if (!activeMenu) {
      sidemenuGeneralCollapsedRef.current.addEventListener('mouseover', handleMouseOverGeneralSection);
      sidemenuGeneralCollapsedRef.current.addEventListener('mouseout', (event) => handleMouseLeave(event));
      sidemenuOperationsCollapsedRef.current.addEventListener('mouseover', handleMouseOverOperationsSection);
      sidemenuOperationsCollapsedRef.current.addEventListener('mouseout', (event) => handleMouseLeave(event));

      if (submenuGeneralCollapsedActive) {
        sidemenuGeneralCollapsedExpandedRef.current.addEventListener('mouseout', handleSidebarExpandedMouseOut);
      }
      if (submenuOperationsCollapsedActive) {
        sidemenuOperationsCollapsedExpandedRef.current.addEventListener('mouseout', handleSidebarExpandedMouseOut);
      }
    }
  }, [activeMenu, submenuGeneralCollapsedActive, submenuOperationsCollapsedActive]);

  useEffect(() => {
    if (isInventoryEventsCallSuccess) {
      if (inventoryEvents.length > 0) {
        setHasInventoryEvents(true);
      } else {
        setHasInventoryEvents(false);
      }
      setUnhandledInventoryEvents(inventoryEvents.length > 99 ? '99+' : inventoryEvents.length);
    }
  }, [isInventoryEventsCallSuccess, inventoryEvents])

  useEffect(() => {
    if (isAgentEventsCallSuccess) {
      if (agentEvents.length > 0) {
        setHasAgentEvents(true);
      } else {
        setHasAgentEvents(false);
      }
      setUnhandledAgentEvents(agentEvents.length > 99 ? '99+' : agentEvents.length);
    }
  }, [isAgentEventsCallSuccess, agentEvents])

  const handleMouseOverGeneralSection = () => {
    currentActiveTargetName = 'General';
    if (!activeMenu) {
      submenuGeneralCollapsedItemExpanded = true;
      setSubmenuGeneralCollapsedActive(true);
    }
  }

  const handleMouseOverOperationsSection = () => {
    currentActiveTargetName = 'Operations';
    if (!activeMenu) {
      submenuOperationsCollapsedItemExpanded = true;
      setSubmenuOperationsCollapsedActive(true);
    }
  }

  const handleMouseLeave = (e) => {
    let elementYMinPosition = e.currentTarget.getClientRects()[0].top;
    let elementYMaxPosition = e.currentTarget.getClientRects()[0].bottom;
    if (e.clientY <= elementYMinPosition || e.clientY >= elementYMaxPosition) {
      if (currentActiveTargetName === 'General') {
        if (!activeMenu) {
          setSubmenuGeneralCollapsedActive(false);
          currentActiveTargetName = null;
        }
      }
      if (currentActiveTargetName === 'Operations') {
        if (!activeMenu) {
          setSubmenuOperationsCollapsedActive(false);
          currentActiveTargetName = null;
        }
      }
    }
  }

  const handleSidebarExpandedMouseOut = () => {
    if (!activeMenu) {
      if (currentActiveTargetName === 'General') {
        submenuGeneralCollapsedItemExpanded = false;
        setSubmenuGeneralCollapsedActive(false);
      }
      if (currentActiveTargetName === 'Operations') {
        submenuOperationsCollapsedItemExpanded = false;
        setSubmenuOperationsCollapsedActive(false);
      }
    }
  }

  const handleCloseSideBar = () => {
    if (activeMenu && screenSize <= 900) {
      setActiveMenu(false);
    }
  }

  return (
    <div className="h-screen 
    md:overflow-hidden overflow-auto
    md:hover:overflow-auto pb-10 bg-[#161e29]">
      {activeMenu && (<>
        <div className='flex flex-row relative'>
          <div className="flex flex-col justify-center
         items-start h-14">
            <Link to={(!user?.tenantInfo.integrationId || user?.tenantInfo.integrationId.trim() === '') ? "/configuration" : "/"} onClick={() => handleCloseSideBar}
              className="items-center gap-3 ml-3 mt-4
          flex text-xl font-extrabold tracking-tight">
              <img src={bitdefenderLogo} alt='' />
            </Link>
            <p className="mt-1 ml-4 font-sans font-family:Roboto,sans-serif dark:text-white text-white uppercase font-semibold">VSA X</p>
          </div>
        </div>
        <div className="mt-10">
          <div className='flex flex-row items-center w-[220px] h-[60px] justify-start'>
            <img className="w-4 h-4 ml-4 mr-4 sidebarIconColorWhite" src={generalIcon} alt='' />
            <p className="text-white font-sans font-family:Roboto,sans-serif">
              General
            </p>
          </div>
          <div className='hover:bg-gray-700 flex flex-row cursor-pointer' id='sideMenu' ref={sidemenuGeneralRef}>
            <div className='h-[30px] flex flex-row items-center justify-between p-[2px] flex-grow'>
              <div className='submenu w-full' ref={sidemenuGeneralExpandedRef}>
                <NavLink
                  to={(!user?.tenantInfo.integrationId || user?.tenantInfo.integrationId.trim() === '') ? "#" : "/dashboard"}
                  onClick={handleCloseSideBar}
                  style={() => ({
                    backgroundColor: locationData.pathname === LocationPaths.DASHBOARD || locationData.pathname === LocationPaths.ROOT ? 'rgba(0, 109, 255, 0.3)' : '',
                    borderLeft: locationData.pathname === LocationPaths.DASHBOARD || locationData.pathname === LocationPaths.ROOT ? '3px solid #006dff' : ''
                  })}
                  className='flex flex-row items-center pl-[53px] text-white text-md h-[30px]'
                >
                  <span className="capitalize pl-2 text-sm">
                    Dashboard
                  </span>
                </NavLink>
              </div>
            </div>
          </div>
          <div className='hover:bg-gray-700 flex flex-row cursor-pointer' id='sideMenu' ref={sidemenuGeneralRef}>
            <div className='h-[30px] flex flex-row items-center justify-between p-[2px] flex-grow'>
              <div className='submenu w-full' ref={sidemenuGeneralExpandedRef}>
                <NavLink
                  to="/configuration"
                  onClick={handleCloseSideBar}
                  style={() => ({
                    backgroundColor: locationData.pathname === LocationPaths.CONFIGURATION ? 'rgba(0, 109, 255, 0.3)' : '',
                    borderLeft: locationData.pathname === LocationPaths.CONFIGURATION ? '3px solid #006dff' : ''
                  })}
                  className='flex flex-row items-center pl-[53px] text-white text-md h-[30px]'
                >
                  <span className="capitalize pl-2 text-sm">
                    Configuration
                  </span>
                </NavLink>
              </div>
            </div>
          </div>
        </div>
        <div className='flex flex-row items-center w-[220px] h-[60px] justify-start'>
          <img className="w-4 h-4 ml-4 mr-4" src={operationsIcon} alt='' />
          <p className="text-white font-sans font-family:Roboto,sans-serif">
            Operations
          </p>
        </div>
        <div className='hover:bg-gray-700 flex flex-row cursor-pointer' id='sideMenu' ref={sidemenuGeneralRef}>
          <div className='h-[30px] flex flex-row items-center justify-between p-[2px] flex-grow'>
            <div className='submenu w-full' ref={sidemenuGeneralExpandedRef}>
              <NavLink
                to={(!user?.tenantInfo.integrationId || user?.tenantInfo.integrationId.trim() === '') ? "#" : "/inventory"}
                onClick={handleCloseSideBar}
                style={() => ({
                  backgroundColor: locationData.pathname === LocationPaths.INVENTORY ? 'rgba(0, 109, 255, 0.3)' : '',
                  borderLeft: locationData.pathname === LocationPaths.INVENTORY ? '3px solid #006dff' : ''
                })}
                className='flex flex-row items-center pl-[53px] text-white text-md h-[30px]'
              >
                <span className="capitalize pl-2 text-sm">
                  Inventory
                </span>
              </NavLink>
            </div>
          </div>
        </div>
        <div className='hover:bg-gray-700 flex flex-row cursor-pointer' id='sideMenu' ref={sidemenuGeneralRef}>
          <div className='h-[30px] flex flex-row items-center justify-between p-[2px] flex-grow'>
            <div className='submenu w-full' ref={sidemenuGeneralExpandedRef}>
              <NavLink
                to={(!user?.tenantInfo.integrationId || user?.tenantInfo.integrationId.trim() === '') ? "#" : "/manageassociations"}
                onClick={handleCloseSideBar}
                style={() => ({
                  backgroundColor: locationData.pathname === LocationPaths.MANAGE_ASSOCIATIONS ? 'rgba(0, 109, 255, 0.3)' : '',
                  borderLeft: locationData.pathname === LocationPaths.MANAGE_ASSOCIATIONS ? '3px solid #006dff' : ''
                })}
                className='flex flex-row items-center pl-[53px] text-white text-md h-[30px]'
              >
                <span className="capitalize pl-2 text-sm">
                  Manage Associations
                </span>
              </NavLink>
            </div>
          </div>
        </div>
        <div className='hover:bg-gray-700 flex flex-row cursor-pointer' id='sideMenu' ref={sidemenuGeneralRef}>
          <div className='h-[30px] flex flex-row items-center justify-between p-[2px] flex-grow'>
            <div className='submenu w-full' ref={sidemenuGeneralExpandedRef}>
              <NavLink
                to={(!user?.tenantInfo.integrationId || user?.tenantInfo.integrationId.trim() === '') ? "#" : "/inventoryevents"}
                onClick={handleCloseSideBar}
                style={() => ({
                  backgroundColor: locationData.pathname === LocationPaths.INVENTORY_EVENTS ? 'rgba(0, 109, 255, 0.3)' : '',
                  borderLeft: locationData.pathname === LocationPaths.INVENTORY_EVENTS ? '3px solid #006dff' : ''
                })}
                className='flex flex-row items-center pl-[53px] text-white text-md h-[30px]'
              >
                <div className='flex flex-row items-center justify-between mr-auto w-full'>
                  <span className="capitalize pl-2 text-sm">
                    Inventory events
                  </span>
                  {hasInventoryEvents && <span className='mr-2 w-[20px] h-[20px] bg-white rounded-sm flex items-center justify-center text-xs text-black'>
                    {unhandledInventoryEvents}
                  </span>}
                </div>
              </NavLink>
            </div>
          </div>
        </div>
        <div className='hover:bg-gray-700 flex flex-row cursor-pointer' id='sideMenu' ref={sidemenuGeneralRef}>
          <div className='h-[30px] flex flex-row items-center justify-between p-[2px] flex-grow'>
            <div className='submenu w-full' ref={sidemenuGeneralExpandedRef}>
              <NavLink
                to={(!user?.tenantInfo.integrationId || user?.tenantInfo.integrationId.trim() === '') ? "#" : "/agentevents"}
                onClick={handleCloseSideBar}
                style={() => ({
                  backgroundColor: locationData.pathname === LocationPaths.AGENT_EVENTS ? 'rgba(0, 109, 255, 0.3)' : '',
                  borderLeft: locationData.pathname === LocationPaths.AGENT_EVENTS ? '3px solid #006dff' : ''
                })}
                className='flex flex-row items-center pl-[53px] text-white text-md h-[30px]'
              >
                <div className='flex flex-row items-center justify-between mr-auto w-full'>
                  <span className="capitalize pl-2 text-sm">
                    Agent events
                  </span>
                  {hasAgentEvents && <span className='mr-2 w-[20px] h-[20px] bg-white rounded-sm flex items-center justify-center text-xs text-black'>
                    {unhandledAgentEvents}
                  </span>}
                </div>
              </NavLink>
            </div>
          </div>
        </div>
      </>)}
      {!activeMenu &&
        <div className='flex flex-col justify-center items-center'>
          <div className='w-[36px] h-[36px] mt-3 mb-16 relative'>
            <img className='absolute top-[1px] left-[6px] w-[23px] h-[28px]' src={bitdefenderIcon} alt='' />
          </div>
          <div className='flex flex-row hover:bg-gray-700' ref={sidemenuGeneralCollapsedRef}
            style={{
              backgroundColor: locationData.pathname === LocationPaths.CONFIGURATION || locationData.pathname === LocationPaths.DASHBOARD || locationData.pathname === LocationPaths.ROOT ? 'rgb(55 65 81 / 1)' : ''
            }}>
            <div className='flex flex-col cursor-pointer items-center justify-center h-[72px] w-[84px]' id='sideMenu'>
              <img className="w-4 h-4 sidebarIconColorWhite" src={generalIcon} alt='' />
              <span className="text-white text-xs/[18px] font-sans mt-2">
                General
              </span>
            </div>
            {submenuGeneralCollapsedActive && <div className='submenu bg-[#202c3d] w-[220px] absolute left-[84px] top-auto min-h-[72px]' ref={sidemenuGeneralCollapsedExpandedRef}>
              <NavLink
                to={(!user?.tenantInfo.integrationId || user?.tenantInfo.integrationId.trim() === '') ? "#" : "/dashboard"}
                onClick={handleCloseSideBar}
                style={() => ({
                  backgroundColor: locationData.pathname === LocationPaths.DASHBOARD || locationData.pathname === LocationPaths.ROOT ? 'rgba(0, 109, 255, 0.3)' : '',
                  borderLeft: locationData.pathname === LocationPaths.DASHBOARD || locationData.pathname === LocationPaths.ROOT ? '3px solid #006dff' : ''
                })}
                className='flex flex-row items-center pl-4 text-white text-md w-[220px] h-[30px] hover:bg-gray-700'
              >
                <span className="capitalize pl-2 text-sm">
                  Dashboard
                </span>
              </NavLink>
              <NavLink
                to="/configuration"
                onClick={handleCloseSideBar}
                style={() => ({
                  backgroundColor: locationData.pathname === LocationPaths.CONFIGURATION ? 'rgba(0, 109, 255, 0.3)' : '',
                  borderLeft: locationData.pathname === LocationPaths.CONFIGURATION ? '3px solid #006dff' : ''
                })}
                className='flex flex-row items-center pl-4 text-white text-md w-[220px] h-[30px] hover:bg-gray-700'
              >
                <span className="capitalize pl-2 text-sm">
                  Configuration
                </span>
              </NavLink>
            </div>}
          </div>
          <div className=' hover:bg-gray-700 flex flex-row' ref={sidemenuOperationsCollapsedRef}
            style={{
              backgroundColor: locationData.pathname !== LocationPaths.DASHBOARD && locationData.pathname !== LocationPaths.ROOT && locationData.pathname !== LocationPaths.CONFIGURATION ? 'rgb(55 65 81 / 1)' : ''
            }}>
            {hasInventoryEvents && <span className='absolute ml-[62px] mt-[10px] w-[10px] h-[10px] bg-white rounded'>
            </span>}
            {hasAgentEvents && <span className='absolute ml-[62px] mt-[10px] w-[10px] h-[10px] bg-white rounded'>
            </span>}
            <div className=' flex flex-col cursor-pointer items-center justify-center h-[72px] w-[84px]' id='sideMenu' >
              <img className="w-4 h-4" src={operationsIcon} alt='' />
              <span className="text-white text-xs/[18px] font-sans mt-2">
                Operations
              </span>
            </div>
            {submenuOperationsCollapsedActive && <div className='submenu bg-[#202c3d] w-[220px] absolute left-[84px] top-auto min-h-[72px]' ref={sidemenuOperationsCollapsedExpandedRef}>
              <NavLink
                to={(!user?.tenantInfo.integrationId || user?.tenantInfo.integrationId.trim() === '') ? "#" : "/inventory"}
                onClick={handleCloseSideBar}
                style={() => ({
                  backgroundColor: locationData.pathname === LocationPaths.INVENTORY ? 'rgba(0, 109, 255, 0.3)' : '',
                  borderLeft: locationData.pathname === LocationPaths.INVENTORY ? '3px solid #006dff' : ''
                })}
                className='flex flex-row items-center pl-4 text-white text-md w-[220px] h-[30px] hover:bg-gray-700'
              >
                <span className="capitalize pl-2 text-sm">
                  Inventory
                </span>
              </NavLink>
              <NavLink
                to={(!user?.tenantInfo.integrationId || user?.tenantInfo.integrationId.trim() === '') ? "#" : "/manageassociations"}
                onClick={handleCloseSideBar}
                style={() => ({
                  backgroundColor: locationData.pathname === LocationPaths.MANAGE_ASSOCIATIONS ? 'rgba(0, 109, 255, 0.3)' : '',
                  borderLeft: locationData.pathname === LocationPaths.MANAGE_ASSOCIATIONS ? '3px solid #006dff' : ''
                })}
                className='flex flex-row items-center pl-4 text-white text-md w-[220px] h-[30px] hover:bg-gray-700'
              >
                <span className="capitalize pl-2 text-sm">
                  Manage Associations
                </span>
              </NavLink>
              <NavLink
                to={(!user?.tenantInfo.integrationId || user?.tenantInfo.integrationId.trim() === '') ? "#" : "/inventoryevents"}
                onClick={handleCloseSideBar}
                style={() => ({
                  backgroundColor: locationData.pathname === LocationPaths.INVENTORY_EVENTS ? 'rgba(0, 109, 255, 0.3)' : '',
                  borderLeft: locationData.pathname === LocationPaths.INVENTORY_EVENTS ? '3px solid #006dff' : ''
                })}
                className='flex flex-row items-center pl-4 text-white text-md w-[220px] h-[30px] hover:bg-gray-700'
              >
                <div className='flex flex-row items-center justify-between mr-auto w-full'>
                  <span className="capitalize pl-2 text-sm">
                    Inventory events
                  </span>
                  {hasInventoryEvents && <span className='mr-2 w-[20px] h-[20px] bg-white text-black rounded-sm flex items-center justify-center text-xs'>
                    {unhandledInventoryEvents}
                  </span>}
                </div>
              </NavLink>
              <NavLink
                to={(!user?.tenantInfo.integrationId || user?.tenantInfo.integrationId.trim() === '') ? "#" : "/agentevents"}
                onClick={handleCloseSideBar}
                style={() => ({
                  backgroundColor: locationData.pathname === LocationPaths.AGENT_EVENTS ? 'rgba(0, 109, 255, 0.3)' : '',
                  borderLeft: locationData.pathname === LocationPaths.AGENT_EVENTS ? '3px solid #006dff' : ''
                })}
                className='flex flex-row items-center pl-4 text-white text-md w-[220px] h-[30px] hover:bg-gray-700'
              >
                <div className='flex flex-row items-center justify-between mr-auto w-full'>
                  <span className="capitalize pl-2 text-sm">
                    Agent events
                  </span>
                  {hasAgentEvents && <span className='mr-2 w-[20px] h-[20px] bg-white text-black rounded-sm flex items-center justify-center text-xs'>
                    {unhandledAgentEvents}
                  </span>}
                </div>
              </NavLink>
            </div>}
          </div>
        </div>}
    </div>
  )
}

export default Sidebar